import { FLAG } from 'enums';
import { PLATFORMS, TAB } from 'constants/general';
import { t } from 'services/i18n.service';
import { cloneElement, useMemo } from 'react';
import Tabs, { Tab, TabList, TabPanel } from 'components/ui/tabs/Tabs';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { setCurrentViewedPack } from 'store/reducer//habit-pack/slice';
import { updateFilterIsSettingToolbarShow } from 'store/reducer/filter/slice';
import { tabBodySelector } from 'store/reducer/setting/selectors';
import { updateActiveTabIndex } from 'store/reducer/setting/slice';
import {
  getRoutineTabs,
  getSettingTabIcon,
  isEditingSpecificRoutine,
  resolveSettingsContentMode
} from 'utils/settings.util';
import { getSettingPageTabContent } from '../tabs-body-steps-revamp/content-body-types';
import { ATTRIB } from 'constants/test';
import { isCurrentPath } from 'utils/routes.util';
import { WEBVIEW_ROUTES } from 'constants/routes';
import AddNewActivityButton from '../tabs-body-steps-revamp/routines/tab-body-header-revamp/tabBody-header-widgets/AddNewActivityButton';

const isRoutineSuggestionsPage = isCurrentPath(
  WEBVIEW_ROUTES.WEBVIEW_ROUTINE_SUGGESTION_REVAMP
);

const TabBodyRevamp = () => {
  const dispatch = useAppDispatch();
  const {
    mode,
    tabs,
    activeTabIndex,
    platform,
    customRoutine,
    flags,
    isUserAdmin,
    packEditorTabMode
  } = useAppSelectorShallow(tabBodySelector);

  const contentMode = useMemo(
    () => resolveSettingsContentMode(mode, packEditorTabMode),
    [mode, packEditorTabMode]
  );

  const isGeekMode = useMemo(() => flags.includes(FLAG.GEEK_MODE), [flags]);

  const routine_tabs = useMemo(
    () =>
      getRoutineTabs(
        tabs,
        platform,
        isGeekMode,
        isUserAdmin,
        customRoutine,
        flags
      ),
    [tabs, platform, isGeekMode, isUserAdmin, customRoutine, flags]
  );

  return (
    <Tabs
      variant='card'
      selectedIndex={activeTabIndex}
      onSelect={(selectedIndex) => {
        dispatch(updateActiveTabIndex(selectedIndex));
        dispatch(updateFilterIsSettingToolbarShow(false));
        if (routine_tabs[selectedIndex] === TAB.HABIT_PACK) {
          dispatch(setCurrentViewedPack(null));
        }
      }}
      selectedTabClassName='tab-selected-tab-revamp'
      data-testid={ATTRIB.DATA_TESTID.SETTINGS_TABS_CONTAINER}>
      <TabList
        className={`tab-list-wrapper-revamp scrollbar-thin-custom ${isEditingSpecificRoutine(customRoutine) && 'hidden'}`}>
        {routine_tabs.map((tab: string, index: number) => {
          const testId = getSettingPageTabContent(
            contentMode,
            index,
            customRoutine,
            platform,
            isUserAdmin,
            flags
          ).testIds?.[index];
          return (
            <Tab
              data-test={testId}
              className={`tab-item-revamp ${platform === PLATFORMS.WEB ? 'tracking-normal' : 'tracking-wider'} ${index === activeTabIndex ? 'bg-secondary-50 dark:bg-primary-dark-200' : 'bg-primary-100 dark:bg-primary-dark-100 hover:bg-primary-200 dark:hover:bg-primary-dark-200'}`}
              key={`tab-${tab}`}
              aria-label={t(`${tab}`)}>
              <span
                aria-hidden='true'
                className={`inline-flex text-primary-400 dark:text-primary-dark-500`}>
                {cloneElement(getSettingTabIcon(tab), {
                  styles: '~w-3.5/3 ~h-3.5/3'
                })}
              </span>
              <p
                className={`${index === activeTabIndex ? 'inline-flex' : 'hidden sm:inline-flex'} ~text-xs/sm`}>
                {t(`${tab}`)}
              </p>
            </Tab>
          );
        })}
        {isRoutineSuggestionsPage && (
          <AddNewActivityButton additionalStyles='ml-auto' />
        )}
      </TabList>
      {routine_tabs.map((tab: string, index: number) => (
        <TabPanel
          key={tab}
          forceRender
          className={`tab-content-wrapper-revamp relative  min-h-[50vh] h-full ~px-2/8 ~py-1/4 animate-fadeIn ${index === activeTabIndex ? 'flex' : 'hidden'}`}>
          {
            getSettingPageTabContent(
              contentMode,
              index,
              customRoutine,
              platform,
              isUserAdmin,
              flags
            ).body
          }
        </TabPanel>
      ))}
    </Tabs>
  );
};

export default TabBodyRevamp;
